import { p as proxyCustomElement, H, c as createEvent, h, d as Host } from './p-BJEvNVLM.js';
import { E as EventEmitter, C as CSSTransition, n as nextTick, i as isNodeEnvironment, h as hasNamedSlot, b as bem } from './p-g1JgJ7wn.js';
import { t as tippy, f as followCursor, s as sticky } from './p-365bzS87.js';
import { r as resolveAppearance } from './p-CkrRd9Ud.js';
import { d as defineCustomElement$3 } from './p-DZLtnVbO.js';
import { d as defineCustomElement$2 } from './p-BMLmP8GU.js';

/**
 * Checks if `value` is `null` or `undefined`.
 *
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is nullish, else `false`.
 * @example
 *
 * isNil(null)
 * // => true
 *
 * isNil(void 0)
 * // => true
 *
 * isNil(NaN)
 * // => false
 *
 * isNil(undefined)
 * // => true
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any,@typescript-eslint/explicit-module-boundary-types
const isNil = (value) => {
    return value == null;
};

/**
 * Tooltip Class
 *
 * Instantiate a tooltip element
 *
 * @method create(options: TooltipOptions): void
 * @method show(): void
 * @method hide(): Promise<void>
 *
 * @emit show(void)
 * @emit hide(void)
 */
const defaultDuration = 300;
const defaultOffset = 8;
class Tooltip {
    constructor() {
        this.emitter = new EventEmitter();
        this.placementMap = {
            left: 'before',
            right: 'after',
            bottom: 'below',
            top: 'above',
        };
        this.cssTransition = new CSSTransition();
    }
    create({ target, content, appendTo, interactive, placement, showOnCreate, follow, sticky: sticky$1, offset = defaultOffset, enterDuration = defaultDuration, leaveDuration = defaultDuration, trigger, }) {
        this.target = target;
        this.leaveDuration = leaveDuration;
        this.enterDuration = enterDuration;
        this.element = content;
        this.element.style.display = 'block';
        content.remove();
        this.tooltipInstance = tippy(this.target, {
            appendTo,
            hideOnClick: false,
            interactive,
            offset: [0, offset],
            placement: this.cosmosToPopperPlacement(placement),
            showOnCreate: showOnCreate || false,
            touch: false,
            delay: [null, this.leaveDuration],
            trigger: trigger || undefined,
            sticky: sticky$1,
            plugins: [followCursor, sticky],
            onMount: ({ popperInstance, popper }) => {
                var _a;
                // The actual placement can be derived from poppers state.
                // If the `follow` option is enabled, we set the placement to `follow`
                // to change the enter animation to a simple fade-in
                const actualPlacement = follow ? 'follow' : popperInstance === null || popperInstance === void 0 ? void 0 : popperInstance.state.placement;
                (_a = popper.firstElementChild) === null || _a === void 0 ? void 0 : _a.setAttribute('data-cosmos-tooltip-placement', this.popperToCosmosPlacement(actualPlacement));
                this.cssTransition.enter(popper.firstElementChild, this.enterDuration);
            },
            onShow: () => {
                this.onShowHandler();
            },
            onUntrigger: async () => {
                await this.onHideHandler();
            },
            render: () => {
                return {
                    popper: this.element,
                };
            },
        });
        if (follow) {
            requestAnimationFrame(() => {
                var _a;
                if (this.tooltipInstance && typeof this.tooltipInstance.setProps === 'function') {
                    (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.setProps({ followCursor: this.getTippyFollowDirection(placement) });
                }
            });
        }
    }
    on(eventName, callback) {
        this.emitter.on(eventName, callback);
    }
    off(eventName, callback) {
        this.emitter.off(eventName, callback);
    }
    show() {
        var _a, _b, _c;
        if ((_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.unmount) {
            (_b = this.tooltipInstance) === null || _b === void 0 ? void 0 : _b.unmount();
        }
        (_c = this.tooltipInstance) === null || _c === void 0 ? void 0 : _c.show();
    }
    async hide() {
        await this.onHideHandler();
        this.tooltipInstance.hide();
    }
    async update() {
        var _a;
        if (!this.isVisible()) {
            return;
        }
        await nextTick();
        await ((_a = this.tooltipInstance.popperInstance) === null || _a === void 0 ? void 0 : _a.update());
    }
    isVisible() {
        return this.tooltipInstance.state.isVisible;
    }
    setProps(props) {
        var _a;
        (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.setProps(props);
    }
    destroy() {
        this.tooltipInstance.destroy();
    }
    popperToCosmosPlacement(placement) {
        return this.placementMap[placement];
    }
    cosmosToPopperPlacement(cosmosPlacement) {
        const popperPlacement = Object.keys(this.placementMap).find((placement) => this.placementMap[placement] === cosmosPlacement);
        return popperPlacement || 'top';
    }
    getTippyFollowDirection(placement) {
        return placement === 'above' || placement === 'below' ? 'horizontal' : 'vertical';
    }
    onShowHandler() {
        this.emitter.emit('show');
        this.target.setAttribute('aria-expanded', 'true');
    }
    onHideHandler() {
        this.emitter.emit('hide');
        return this.triggerCssLeaveTransition();
    }
    triggerCssLeaveTransition() {
        return this.cssTransition.leave(this.element.firstElementChild, this.leaveDuration);
    }
}

const cosmosTooltipCss = "@supports (font-variation-settings: normal){::slotted(strong),::slotted(b){font-variation-settings:\"opsz\" 12, \"wght\" 700}}:host{display:contents}.tippy-mount{display:inline-block;height:0;width:0}.tippy-element{display:none;z-index:var(--cosmos-tooltip-z-index, 9999) !important}.cosmos-tooltip{--cosmos-text-color:var(--cosmos-tooltip-color);--cosmos-text-font-family:var(--cosmos-tooltip-font-family);--cosmos-text-font-size:var(--cosmos-tooltip-font-size);--cosmos-text-font-style:var(--cosmos-tooltip-font-style);--cosmos-text-font-weight:var(--cosmos-tooltip-font-weight);--cosmos-text-letter-spacing:var(--cosmos-tooltip-letter-spacing);--cosmos-text-line-height:var(--cosmos-tooltip-line-height);--cosmos-text-text-transform:var(--cosmos-tooltip-text-transform);background-color:var(--cosmos-tooltip-background, #FFFFFF);border-color:var(--cosmos-tooltip-border-color, transparent);border-radius:var(--cosmos-tooltip-border-radius, 6px);border-style:solid;border-width:var(--cosmos-tooltip-border-width, 0);box-shadow:var(--cosmos-tooltip-box-shadow, 0px 16px 24px 0px rgba(0, 15, 30, 0.0588235294), 0px 4px 8px 0px rgba(0, 15, 30, 0.0196078431));max-width:calc(288rem / var(--cosmos-base-font-size, 16));padding:var(--cosmos-tooltip-padding, 4px 8px)}.cosmos-tooltip--spacing-loose{padding:var(--cosmos-tooltip-padding, 8px 12px)}.cosmos-tooltip--appearance-dark{background-color:var(--cosmos-tooltip-background, #00162B)}.cosmos-tooltip.enter{opacity:0}.cosmos-tooltip[data-cosmos-tooltip-placement=above].enter{transform:translate(0, 8px)}.cosmos-tooltip[data-cosmos-tooltip-placement=below].enter{transform:translate(0, -8px)}.cosmos-tooltip[data-cosmos-tooltip-placement=before].enter{transform:translate(8px, 0)}.cosmos-tooltip[data-cosmos-tooltip-placement=after].enter{transform:translate(-8px, 0)}.cosmos-tooltip[data-cosmos-tooltip-placement].enter-active{opacity:1;transform:translate(0, 0);transition:0.15s cubic-bezier(0.33, 1, 0.68, 1)}.cosmos-tooltip.leave{opacity:1}.cosmos-tooltip.leave-active{opacity:0;transition:0.15s cubic-bezier(0, 0.32, 0.32, 1)}.cosmos-tooltip--empty{display:none}";

const generateClassNames = bem('cosmos-tooltip');
const CosmosTooltip$1 = /*@__PURE__*/ proxyCustomElement(class CosmosTooltip extends H {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.hideEvent = createEvent(this, "hide", 7);
        this.showEvent = createEvent(this, "show", 7);
        /**
         * Use this to set the content of the tooltip.
         */
        this.content = '';
        /**
         * Disables the Tooltip to show and hide on hover and focus gesture but instead to be controlled via the visible property.
         */
        this.controlled = false;
        /**
         * When set to true  or cursor , the tooltip will appear and follow the mouse cursor along the x-axis when positioned below or above the target and the y-axis when displayed before or after.
         *
         * When set to target the tooltip will stick to the position of its target and follow it along while its position might change.
         *
         * Both options require the tooltip to update its position continuously and might have performance implications.
         */
        this.follow = false;
        /**
         * The distance in pixels between the tooltip and its target.
         */
        this.offset = 8;
        /**
         * The preferred placement of the tooltip. Note that the actual placement may vary as needed to keep the tooltip inside of the viewport.
         */
        this.placement = 'above';
        /**
         * Use this to set the spacing of the tooltip.
         */
        this.spacing = 'tight';
        /**
         * Makes the tooltip disappear when the pointer leaves the target.
         */
        this.unreachable = false;
        /**
         * Sets to Tooltip's visibility when used in conjunction with the controlled property
         */
        this.visible = false;
        this.computedAppearance = 'light';
        this.hasContent = false;
        this.tippyElement = undefined;
        this.tippyMount = undefined;
        this.tooltipInstance = undefined;
        this.onDefaultSlotChange = () => {
            if (this.tooltipInstance && !this.getTooltipTarget) {
                this.tooltipInstance.destroy();
                this.tooltipInstance = undefined;
            }
            else {
                this.instantiateTooltip();
                this.createTooltip();
            }
        };
        this.showHandler = () => {
            this.showEvent.emit();
        };
        this.hideHandler = () => {
            this.hideEvent.emit();
        };
    }
    handleAppearanceChange(appearance) {
        this.computedAppearance = appearance;
    }
    handleShowChange() {
        var _a, _b, _c, _d;
        /* Setting touch to true in order to make sure that the tooltip will also be shown on mobile/touch devices (tippy checks for this before showing) */
        (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.setProps({ touch: true });
        if (this.visible) {
            (_b = this.tooltipInstance) === null || _b === void 0 ? void 0 : _b.show();
        }
        else {
            (_c = this.tooltipInstance) === null || _c === void 0 ? void 0 : _c.hide();
        }
        (_d = this.tooltipInstance) === null || _d === void 0 ? void 0 : _d.setProps({ touch: false });
    }
    async handleContentChange() {
        await this.update();
    }
    /**
     * Triggers an update of the tooltip positioning.
     */
    async update() {
        var _a;
        if (!this.tooltipInstance) {
            return;
        }
        await ((_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.update());
    }
    createObserver() {
        const contentSlot = this.element.querySelector('[slot="content"]');
        if (!contentSlot || typeof MutationObserver === 'undefined') {
            return;
        }
        const mutationObserver = new MutationObserver((mutationList) => {
            mutationList.forEach((mutation) => {
                if (mutation.type === 'childList') {
                    this.update();
                }
            });
        });
        mutationObserver.observe(contentSlot, {
            childList: true,
            subtree: true,
        });
        this.observer = mutationObserver;
    }
    instantiateTooltip() {
        if (isNodeEnvironment() || !this.getTooltipTarget()) {
            return;
        }
        this.tooltipInstance = new Tooltip();
    }
    createTooltip() {
        var _a;
        if (!this.tooltipInstance) {
            return;
        }
        this.tooltipOptions = {
            target: this.getTooltipTarget(),
            appendTo: this.tippyMount,
            showOnCreate: this.visible,
            placement: this.placement || 'above',
            content: this.tippyElement,
            follow: this.normalizedFollowValue,
            sticky: this.follow === 'target',
            interactive: !this.unreachable,
            offset: this.offset,
            enterDuration: 150,
            leaveDuration: 150,
            trigger: this.controlled ? 'manual' : undefined,
        };
        (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.create(this.tooltipOptions);
        this.addTooltipListeners();
    }
    addTooltipListeners() {
        var _a, _b;
        if (!this.tooltipInstance) {
            return;
        }
        (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.on('show', this.showHandler);
        (_b = this.tooltipInstance) === null || _b === void 0 ? void 0 : _b.on('hide', this.hideHandler);
    }
    removeTooltipListeners() {
        var _a, _b;
        if (!this.tooltipInstance) {
            return;
        }
        (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.off('show', this.showHandler);
        (_b = this.tooltipInstance) === null || _b === void 0 ? void 0 : _b.off('hide', this.hideHandler);
    }
    getTooltipTarget() {
        return Array.from(this.element.children).find((el) => el.tagName.toLowerCase() !== 'style' && el.getAttribute('slot') !== 'content');
    }
    getContent() {
        return (this.content && (h("cosmos-text", { size: "x-small", weight: "medium", exportparts: "base: text" }, this.content)));
    }
    determineIfTooltipHasContent() {
        const hasContentSlot = hasNamedSlot(this.element, 'content');
        this.hasContent = (!isNil(this.content) && this.content !== '') || hasContentSlot;
    }
    /**
     * Since the follow value can be a string or a boolean, we need to normalize it
     * and split it since in tippy follow and sticky are two different options.
     */
    normalizeFollowValue() {
        return ['true', '', 'cursor'].includes(this.follow);
    }
    componentWillLoad() {
        this.instantiateTooltip();
        resolveAppearance({
            instance: this,
            modeToAppearanceMap: { dark: 'dark', light: 'light' },
            callback: this.handleAppearanceChange.bind(this),
        });
    }
    componentDidLoad() {
        this.createTooltip();
        this.createObserver();
    }
    componentWillRender() {
        this.normalizedFollowValue = this.normalizeFollowValue();
        this.determineIfTooltipHasContent();
    }
    disconnectedCallback() {
        var _a, _b;
        this.removeTooltipListeners();
        (_a = this.tooltipInstance) === null || _a === void 0 ? void 0 : _a.destroy();
        (_b = this.observer) === null || _b === void 0 ? void 0 : _b.disconnect();
    }
    render() {
        return (h(Host, { key: '16bbb2d3686e1188bb9432a5355cfb30a003f3f1' }, h("div", { key: '431f1fb7b1951a5db739789dc2c4799901942868', class: "tippy-mount", ref: (element) => (this.tippyMount = element) }), h("div", { key: '7be4ef92418c425cd73bd5ea87d045a9e8bd7dea', class: "tippy-element", ref: (element) => (this.tippyElement = element) }, h("div", { key: 'fd3b2f51944875c97c2e9e74fc3c61b86cb5a32d', class: generateClassNames({
                spacing: this.spacing,
                appearance: this.computedAppearance,
                unreachable: this.unreachable,
                empty: !this.hasContent,
            }), part: "base" }, h("cosmos-mode", { key: 'dcef0cf3dd852f008418ce05bc4827df8bc247ae', mode: this.computedAppearance }, h("span", { key: 'c97d17d6d334a57ea3a472d75df6968057a3073f' }, h("slot", { key: '912108c0d43f21a34bfd8567b950e89b3ce2be42', name: "content", onSlotchange: () => this.determineIfTooltipHasContent() }, this.getContent()))))), h("slot", { key: '4d71cf9aca1972e04b3b392264d7b7a2f5096799', onSlotchange: this.onDefaultSlotChange })));
    }
    get element() { return this; }
    static get watchers() { return {
        "appearance": ["handleAppearanceChange"],
        "visible": ["handleShowChange"],
        "content": ["handleContentChange"]
    }; }
    static get style() { return cosmosTooltipCss; }
}, [1, "cosmos-tooltip", {
        "appearance": [513],
        "content": [513],
        "controlled": [516],
        "follow": [520],
        "offset": [514],
        "placement": [513],
        "spacing": [513],
        "unreachable": [516],
        "visible": [516],
        "computedAppearance": [32],
        "hasContent": [32],
        "update": [64]
    }, undefined, {
        "appearance": ["handleAppearanceChange"],
        "visible": ["handleShowChange"],
        "content": ["handleContentChange"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["cosmos-tooltip", "cosmos-mode", "cosmos-text"];
    components.forEach(tagName => { switch (tagName) {
        case "cosmos-tooltip":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, CosmosTooltip$1);
            }
            break;
        case "cosmos-mode":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "cosmos-text":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}

const CosmosTooltip = CosmosTooltip$1;
const defineCustomElement = defineCustomElement$1;

export { CosmosTooltip, defineCustomElement };
